import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Link as LinkIcon,
  Heading1, Heading2, Heading3, Table as TableIcon,
  Paintbrush, Highlighter, Undo, Redo, RemoveFormatting,
  Plus, Trash2, Rows3
} from 'lucide-react';

const COLORS = ['#000000','#DC2626','#EA580C','#CA8A04','#16A34A','#2563EB','#7C3AED','#DB2777','#64748B'];
const HIGHLIGHTS = ['#FEF08A','#BBF7D0','#BFDBFE','#E9D5FF','#FECDD3','#FED7AA'];

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1 rounded transition-colors ${
        active ? 'bg-[#FF6B35] text-white' : 'text-slate-600 hover:bg-slate-200'
      } ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );
}

function ColorPicker({ colors, onSelect, activeColor, icon: Icon, title }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen(!open)}
        title={title}
        className={`p-1 rounded transition-colors ${open ? 'bg-slate-200' : 'text-slate-600 hover:bg-slate-200'}`}
      >
        <Icon className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-1.5 flex gap-1 z-50 flex-wrap w-[120px]">
          {colors.map(c => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onSelect(c); setOpen(false); }}
              className={`w-5 h-5 rounded border ${activeColor === c ? 'ring-2 ring-[#FF6B35] ring-offset-1' : 'border-slate-300'}`}
              style={{ backgroundColor: c }}
            />
          ))}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onSelect(null); setOpen(false); }}
            title="Remove"
            className="w-5 h-5 rounded border border-slate-300 flex items-center justify-center text-slate-400 text-xs"
          >
            x
          </button>
        </div>
      )}
    </div>
  );
}

function Toolbar({ editor }) {
  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  if (!editor) return null;

  const iconSize = "h-3.5 w-3.5";

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/80 rounded-t-md" data-testid="rich-text-toolbar">
      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo className={iconSize} />
      </ToolbarButton>

      <div className="w-px h-4 bg-slate-300 mx-0.5" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
        <Bold className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
        <Italic className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
        <UnderlineIcon className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
        <Strikethrough className={iconSize} />
      </ToolbarButton>

      <div className="w-px h-4 bg-slate-300 mx-0.5" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
        <Heading1 className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
        <Heading2 className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
        <Heading3 className={iconSize} />
      </ToolbarButton>

      <div className="w-px h-4 bg-slate-300 mx-0.5" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
        <List className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
        <ListOrdered className={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
        <Quote className={iconSize} />
      </ToolbarButton>

      <div className="w-px h-4 bg-slate-300 mx-0.5" />

      <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Link">
        <LinkIcon className={iconSize} />
      </ToolbarButton>

      <ColorPicker
        colors={COLORS}
        onSelect={(c) => c ? editor.chain().focus().setColor(c).run() : editor.chain().focus().unsetColor().run()}
        activeColor={editor.getAttributes('textStyle').color}
        icon={Paintbrush}
        title="Text Color"
      />
      <ColorPicker
        colors={HIGHLIGHTS}
        onSelect={(c) => c ? editor.chain().focus().toggleHighlight({ color: c }).run() : editor.chain().focus().unsetHighlight().run()}
        activeColor={editor.isActive('highlight') ? (editor.getAttributes('highlight').color || '#FEF08A') : null}
        icon={Highlighter}
        title="Highlight"
      />

      <div className="w-px h-4 bg-slate-300 mx-0.5" />

      <ToolbarButton onClick={addTable} title="Insert Table">
        <TableIcon className={iconSize} />
      </ToolbarButton>
      {editor.isActive('table') && (
        <>
          <ToolbarButton onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add Column">
            <Plus className={iconSize} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().addRowAfter().run()} title="Add Row">
            <Rows3 className={iconSize} />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().deleteTable().run()} title="Delete Table">
            <Trash2 className={iconSize} />
          </ToolbarButton>
        </>
      )}

      <div className="w-px h-4 bg-slate-300 mx-0.5" />

      <ToolbarButton onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear Formatting">
        <RemoveFormatting className={iconSize} />
      </ToolbarButton>
    </div>
  );
}

export default function RichTextEditor({ value, onChange, placeholder, readOnly, className, 'data-testid': testId }) {
  const isInternalChange = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Disable built-in extensions we're replacing with custom configs
      }),
      Underline,
      Link.configure({ 
        openOnClick: false, 
        HTMLAttributes: { class: 'text-blue-600 underline' },
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      if (onChange) {
        isInternalChange.current = true;
        const html = ed.getHTML();
        onChange(html === '<p></p>' || html === '' ? '' : html);
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap focus:outline-none min-h-[80px] px-3 py-2 text-sm',
        'data-placeholder': placeholder || 'Enter text...',
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (!editor) return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const currentHtml = editor.getHTML();
    const nv = value || '';
    if (nv !== currentHtml && !(nv === '' && currentHtml === '<p></p>')) {
      editor.commands.setContent(nv, false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  return (
    <div
      className={`border border-slate-200 rounded-md overflow-hidden bg-white ${readOnly ? 'opacity-70' : ''} ${className || ''}`}
      data-testid={testId ? `${testId}-wrapper` : 'rich-text-editor'}
    >
      {!readOnly && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}
